<script setup lang="ts">
import { computed, watch } from "vue";
import { useSelector } from "@xstate/vue";
import { useRoute, useRouter } from "vue-router";

import { RsLoading, RsError } from "@/components";

import { useLoadingStore } from "@/stores/loading-store";
import { BrandStatus, useBrandStore } from "@/stores/brand-store";

import { authMachine } from "@/machines/auth-machine";

import { ModuleStatus } from "@/models/ModuleStatus";

const loadingId = "loading-brand";

const route = useRoute();
const router = useRouter();
const brandStore = useBrandStore();
const loadingStore = useLoadingStore();

const hasAuthed = useSelector(authMachine, (snapshot) => snapshot.hasTag("AuthComplete"));

const brand = computed(() => route.params.brand as string);

const readyToLoad = computed(() => {
  return brandStore.readyToLoad;
});
const moduleStatus = computed(() => {
  return brandStore.moduleStatus;
});
const hasBrandError = computed(() => {
  return brandStore.moduleStatus === BrandStatus.NoAccess || brandStore.moduleStatus === BrandStatus.MissingBrand;
});

// In this route, the brand param will always be set
router.beforeEach((to, from) => {
  // If this is defined, it means we've completed the redirection once, removing the lens for defaults.
  if (to.redirectedFrom != null) return;

  const fromBrand = (from.params.brand as string | undefined)?.toLowerCase();
  const toBrand = (to.params.brand as string | undefined)?.toLowerCase();
  // If the "to" and "from" route are targeted at the same brand, and
  //    the "to" route doesn't have a lens, and
  //    the "from" route has a lens, and
  //    the lens wasn't forcefully removed e.g. while selecting the default lens,
  // then we should copy the lens over
  if (fromBrand === toBrand && from.query.lens != null && to.query.lens == null && to.query.forceLensRemove == null) {
    return { ...to, query: { ...to.query, lens: from.query.lens } };
  }

  // But in this case, the lens was forcefully removed, so in this case it should be undefined,
  // but we won't control that still. Just clean up the forceLensRemove query.
  if (to.query.forceLensRemove != null) {
    return { ...to, query: { ...to.query, forceLensRemove: undefined } };
  }
});

router.afterEach((to, from) => {
  if (to.name !== "brand" || from.params.brand === to.params.brand) return;
  location.reload();
});

watch(
  [brand, hasAuthed, moduleStatus],
  () => {
    switch (moduleStatus.value) {
      case ModuleStatus.Initial:
      case ModuleStatus.Loading:
        loadingStore.updateLoadingMessage(loadingId, "Identifying ...");
        break;
      case BrandStatus.LoadingConfiguration:
        loadingStore.updateLoadingMessage(loadingId, "Loading configuration");
        break;
      case BrandStatus.MappingLoaded:
        if (hasAuthed.value) brandStore.onBrandSelected();
        break;
    }
  },
  { immediate: true },
);
</script>

<template>
  <RsError v-if="hasBrandError" code="300" />
  <RsLoading v-else-if="!readyToLoad" :id="loadingId" />
  <router-view v-else :ready-to-load="readyToLoad" />
</template>
